// ===== BEGIN FILE: src/components/report-v2/ReportIndexPage.tsx =====
/**
 * Report Index / Table of Contents page — appears after the intro page.
 * Shows:
 *  • Report strengths / what makes it unique
 *  • How to use the report
 *  • Full section index with visual links
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import type { ReportData } from './types';

interface ReportIndexPageProps {
  data: ReportData;
  /** When true, AI-analysis sections appear in the index */
  isAdmin?: boolean;
  /** When true, shows SWOT and Detailed sections */
  hasSwot?: boolean;
  hasDetailed?: boolean;
  hasCurrentPeriod?: boolean;
  /** Called when a section row is clicked — scroll is handled by the parent */
  onSectionClick?: (sectionId: string) => void;
}

const BRAND = {
  purple: '#5B21B6',
  purpleLight: '#7C3AED',
  purpleSoft: '#F5F3FF',
  gold: '#F59E0B',
  goldLight: '#FEF9C3',
  text: '#1F2937',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  success: '#047857',
  successSoft: '#ECFDF5',
};

interface IndexEntry {
  id: string;
  number: string;
  title: string;
  description: string;
  icon: string;
  accent: string;
  adminOnly?: boolean;
}

interface GroupHeader {
  isGroup: true;
  label: string;
  subtitle: string;
  icon: string;
  color: string;
}
type IndexRow = IndexEntry | GroupHeader;

const SECTIONS: IndexEntry[] = [
  {
    id: 'section-birth-details',
    number: '01',
    title: 'Birth Details & Essentials',
    description: 'Ascendant, Moon sign, nakshatra, chart overview and core planetary snapshot.',
    icon: '🌟',
    accent: BRAND.purple,
  },
  {
    id: 'section-chart-d1',
    number: '02',
    title: 'Vedic Birth Chart (D1 & D9)',
    description: 'North Indian chart render with both D1 Rāśi and D9 Navāmśa side-by-side.',
    icon: '🗺️',
    accent: '#0369A1',
  },
  {
    id: 'section-planet-details',
    number: '03',
    title: 'Planet Details',
    description: 'Complete sidereal longitudes, house placements, nakshatra pāda, retrograde & combustion status.',
    icon: '🪐',
    accent: '#7C3AED',
  },
  {
    id: 'section-planet-factors',
    number: '04',
    title: 'Net Planetary Impact',
    description: 'Six-fold strength (Shadbala), Ishta/Kashta Phala and blended scores for every graha.',
    icon: '⚖️',
    accent: '#B45309',
  },
  {
    id: 'section-house-scores',
    number: '05',
    title: 'House Strength Analysis',
    description: 'Bhava Bala scores for all 12 houses with life-area significance and natural benefic occupancy.',
    icon: '🏛️',
    accent: '#065F46',
  },
  {
    id: 'section-swot',
    number: '06',
    title: 'SWOT Analysis (AI)',
    description: 'AI-generated strengths, weaknesses, opportunities and threats from your natal chart.',
    icon: '🧠',
    accent: BRAND.purple,
    adminOnly: true,
  },
  {
    id: 'section-d1-house-scores',
    number: '07',
    title: 'House Balance & Karmic Activities / Remedies',
    description: 'Practical activities, remedies and daily practices to strengthen weak houses and balance your karma.',
    icon: '🔄',
    accent: '#9D174D',
  },
  {
    id: 'section-life-areas',
    number: '08',
    title: 'Life Areas Dashboard & Remedies',
    description: '12 life domains — career, wealth, relationships, health and more — with scored guidance and remedies.',
    icon: '🗂️',
    accent: '#1D4ED8',
  },
  {
    id: 'section-detailed',
    number: '09',
    title: 'Detailed Chart Analysis (AI)',
    description: 'In-depth AI narrative covering each house lord, key planetary combinations and their impact.',
    icon: '📋',
    accent: BRAND.success,
    adminOnly: true,
  },
  {
    id: 'section-yogas',
    number: '10',
    title: 'Classical Yogas',
    description: 'Detected Raj, Dhana, Mahapurusha, Parivartana and Arishta yogas with their effects.',
    icon: '🔮',
    accent: '#6D28D9',
  },
  {
    id: 'section-dasha-timeline',
    number: '11',
    title: 'Vimshottari Dasha Timeline',
    description: 'Current and upcoming Mahādasha, Antardasha & Pratyantar periods with activated life areas.',
    icon: '⏳',
    accent: '#0F766E',
  },
  {
    id: 'section-year-forecast',
    number: '12',
    title: 'Year at a Glance — Life Areas Forecast',
    description: 'Rolling 12-month colour-coded forecast across Health, Wealth, Career, Business, Marriage, Children, Property and Education — driven by dasha periods and transits.',
    icon: '📅',
    accent: '#5B21B6',
  },
  {
    id: 'section-year-forecast-ai',
    number: '13',
    title: 'Year-ahead Monthly Predictions (AI)',
    description: 'AI-generated month-by-month narrative for the next 12 months — dasha lords, transits, yogas, doshas and priority life areas woven into personalised guidance.',
    icon: '🔭',
    accent: '#0369A1',
    adminOnly: true,
  },
  {
    id: 'section-opportunity-windows',
    number: '14',
    title: 'Opportunity & Caution Windows',
    description: 'Feed-based prediction windows for the coming months — areas to capitalise on and to tread carefully.',
    icon: '🪟',
    accent: '#0369A1',
  },
  {
    id: 'section-faq',
    number: '15',
    title: 'Ask What the Stars Already Know',
    description: '8 of the most searched life questions — relationships, career, wealth, health, children and more — and why your chart holds the answers.',
    icon: '🤫',
    accent: '#5B21B6',
  },
  {
    id: 'section-stay-proactive',
    number: '16',
    title: 'Continue Your Journey with ShreemAstro™',
    description: 'Daily forecasts, personalised notifications, chat with AI astrologer, family charts and more — your ongoing cosmic companion.',
    icon: '🚀',
    accent: '#047857',
  },
];

/** Report strength features list */
const REPORT_STRENGTHS = [
  { icon: '✦', text: 'Fully computed using classical Lahiri ayanamsa sidereal calculations — the gold standard of Vedic astrology' },
  { icon: '✦', text: 'Six-fold Shadbala planetary strength analysis — rare in consumer-grade reports' },
  { icon: '✦', text: 'Classical yoga detection across 40+ combinations (Raj, Dhana, Mahapurusha, Vipareeta, Parivartana)' },
  { icon: '✦', text: 'Gate-based prediction engine (5-gate algorithm) for accurate opportunity and caution window forecasting' },
  { icon: '✦', text: 'House balance activities rooted in Sanskrit remedial wisdom — practical, not just predictive' },
  { icon: '✦', text: 'D1 + D9 charts side-by-side — the two most important divisional lenses for destiny and marriage' },
];

/** How to use guidance entries */
const HOW_TO_USE = [
  { step: '1', tip: 'Start with your Ascendant and Moon sign (Sect. 01) — they are your "operating system".' },
  { step: '2', tip: 'Cross-reference your House Strength scores (Sect. 05) with Life Areas (Sect. 07) to find your power zones.' },
  { step: '3', tip: 'Check Yogas (Sect. 08) — even one strong yoga can override many weak placements.' },
  { step: '4', tip: 'Use Dasha Timeline (Sect. 09) to understand *when* the chart\'s promises manifest.' },
  { step: '5', tip: 'Read Year Forecast (Sect. 10) to spot your power months before they arrive — act during green months, consolidate during red.' },
  { step: '6', tip: 'Act on Opportunity Windows (Sect. 14) — these are your highest-ROI months to take initiative.' },
];

export const ReportIndexPage: React.FC<ReportIndexPageProps> = ({
  data,
  isAdmin,
  hasSwot,
  hasDetailed,
  hasCurrentPeriod,
  onSectionClick,
}) => {
  const visibleSections = SECTIONS.filter(s => {
    if (!s.adminOnly) return true;
    if (s.id === 'section-swot') return isAdmin && hasSwot;
    if (s.id === 'section-detailed') return isAdmin && hasDetailed;
    if (s.id === 'section-year-forecast-ai') return isAdmin && hasCurrentPeriod;
    return false;
  });

  const GROUPS: GroupHeader[] = [
    {
      isGroup: true,
      label: 'How Am I Wired',
      subtitle: 'My chart & planets at birth',
      icon: '🌌',
      color: '#5B21B6',
    },
    {
      isGroup: true,
      label: 'My Karmic Story',
      subtitle: 'House strength, life areas & remedies',
      icon: '🏛️',
      color: '#065F46',
    },
    {
      isGroup: true,
      label: 'My Life Path Ahead',
      subtitle: 'Yogas, dashas, year forecast & opportunity windows',
      icon: '🛤️',
      color: '#B45309',
    },
    {
      isGroup: true,
      label: 'Stay Proactive',
      subtitle: 'ShreemAstro™ live predictions, notifications & more',
      icon: '🚀',
      color: '#047857',
    },
  ];

  // Build rows with group headers injected before their first section
  const GROUP_STARTS: Record<string, number> = {
    'section-birth-details': 0,    // Group 0: How Am I Wired — before §01
    'section-house-scores': 1,     // Group 1: My Karmic Story — before §05 (House Strength, SWOT, Detailed, House Balance, Life Areas)
    'section-yogas': 2,            // Group 2: My Life Path Ahead — before §10
    'section-faq': 3,              // Group 3: Stay Proactive — before §15 (FAQ) and §16 (closing)
  };

  const indexRows: IndexRow[] = [];
  visibleSections.forEach(s => {
    const gIndex = GROUP_STARTS[s.id];
    if (gIndex !== undefined) indexRows.push(GROUPS[gIndex]);
    indexRows.push(s);
  });

  return (
    <Box
      className="report-section"
      sx={{
        background: '#FFFFFF',
        minHeight: '297mm',
        p: '14mm 12mm 12mm',
        // NOTE: no breakBefore here — the .report-section CSS class handles the page break.
        '@media print': {
          minHeight: 'unset',  // removing 297mm forces real content height, avoids blank overflow page
          p: '14mm 12mm 12mm',
        },
      }}
    >
      {/* ── Page header band ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `3px solid ${BRAND.purple}`,
          pb: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: BRAND.purple, lineHeight: 1.2 }}>
            📑 Report Index
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: BRAND.textMuted, mt: 0.25 }}>
            {data.birthDetails.name} · Comprehensive Vedic Analysis
          </Typography>
        </Box>
        <Box
          sx={{
            background: BRAND.purpleSoft,
            border: `1px solid ${BRAND.purple}22`,
            borderRadius: '8px',
            px: 1.5,
            py: 0.75,
            textAlign: 'right',
          }}
        >
          <Typography sx={{ fontSize: '0.7rem', color: BRAND.textMuted }}>Generated</Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: BRAND.purple }}>
            {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </Typography>
        </Box>
      </Box>

      {/* ── Top row: Strengths + How to use ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>

        {/* What makes this report unique */}
        <Box
          sx={{
            background: BRAND.purpleSoft,
            border: `1px solid ${BRAND.purple}33`,
            borderRadius: '10px',
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.82rem',
              color: BRAND.purple,
              mb: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            <span>💎</span> What Makes This Report Unique
          </Typography>
          {REPORT_STRENGTHS.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.85, alignItems: 'flex-start' }}>
              <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700, fontSize: '0.7rem', mt: '3px', flexShrink: 0 }}>
                {s.icon}
              </Box>
              <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.55, color: BRAND.text }}>
                {s.text}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* How to use this report */}
        <Box
          sx={{
            background: BRAND.goldLight,
            border: `1px solid ${BRAND.gold}44`,
            borderRadius: '10px',
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.82rem',
              color: '#92400E',
              mb: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            <span>🧭</span> How to Use This Report
          </Typography>
          {HOW_TO_USE.map((h, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.25, mb: 1, alignItems: 'flex-start' }}>
              <Box
                component="span"
                sx={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: BRAND.gold,
                  color: '#fff',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: '1px',
                }}
              >
                {h.step}
              </Box>
              <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.55, color: '#78350F' }}>
                {h.tip}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Section index ── */}
      <Box
        sx={{
          border: `1px solid ${BRAND.border}`,
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '50px 1fr',
            gap: 0,
            background: `linear-gradient(90deg, ${BRAND.purple}, #4C1D95)`,
            px: 1.75,
            py: 1,
            '@media print': {
              background: BRAND.purple,
            },
          }}
        >
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>
            #
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>
            Section
          </Typography>
        </Box>

        {/* Table rows */}
        {indexRows.map((row, idx) => {
          // ── Group header row ──
          if ('isGroup' in row) {
            return (
              <Box
                key={`group-${idx}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.75,
                  py: 0.75,
                  background: `${row.color}12`,
                  borderBottom: `1px solid ${row.color}30`,
                  borderTop: idx > 0 ? `2px solid ${row.color}40` : 'none',
                }}
              >
                <Box component="span" sx={{ fontSize: '0.85rem' }}>{row.icon}</Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: row.color, lineHeight: 1.2, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: BRAND.textMuted, lineHeight: 1.2 }}>
                    {row.subtitle}
                  </Typography>
                </Box>
              </Box>
            );
          }
          // ── Section row ──
          const section = row as IndexEntry;
          const sIdx = visibleSections.indexOf(section);
          return (
          <Box
            key={section.id}
            component="a"
            href={`#${section.id}`}
            onClick={(e: React.MouseEvent) => {
              if (onSectionClick) {
                e.preventDefault();
                onSectionClick(section.id);
              }
              // No preventDefault when no handler — lets native anchor work in exported HTML
            }}
            sx={{
              display: 'grid',
              gridTemplateColumns: '50px 1fr',
              gap: 0,
              px: 1.75,
              py: 1,
              background: sIdx % 2 === 0 ? '#FAFAFA' : '#FFFFFF',
              borderBottom: sIdx < visibleSections.length - 1 ? `1px solid ${BRAND.border}` : 'none',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              cursor: 'pointer',
              transition: 'background 0.15s',
              '&:hover': {
                background: `${section.accent}10`,
              },
              '@media print': {
                breakInside: 'avoid',
                pageBreakInside: 'avoid',
                pointerEvents: 'none',
                textDecoration: 'none',
              },
            }}
          >
            {/* Icon + number + arrow badge — single horizontal line */}
            <Box
              component="span"
              sx={{
                width: '46px',
                height: '22px',
                borderRadius: '6px',
                background: section.accent,
                color: '#fff',
                display: 'inline-flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '3px',
                flexShrink: 0,
              }}
            >
              <Box component="span" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>{section.icon}</Box>
              <Box component="span" sx={{ fontSize: '0.55rem', fontWeight: 800, lineHeight: 1 }}>{section.number}</Box>
              <Box component="span" sx={{ fontSize: '0.6rem', lineHeight: 1, opacity: 0.85 }}>↗</Box>
            </Box>

            {/* Title + description */}
            <Box sx={{ pl: 1.25 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  color: BRAND.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box component="span" sx={{ fontSize: '0.8rem' }}>{section.icon}</Box>
                {section.title}
                {section.adminOnly && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      background: BRAND.purpleSoft,
                      color: BRAND.purple,
                      fontSize: '0.56rem',
                      fontWeight: 700,
                      px: '5px',
                      py: '1px',
                      borderRadius: '4px',
                      letterSpacing: '0.04em',
                    }}
                  >
                    AI
                  </Box>
                )}
              </Typography>
              <Typography sx={{ fontSize: '0.67rem', color: BRAND.textMuted, lineHeight: 1.4, mt: 0.2 }}>
                {section.description}
              </Typography>
            </Box>


          </Box>
          );
        })}
      </Box>

      {/* ── Bottom note ── */}
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          background: BRAND.successSoft,
          border: `1px solid #A7F3D0`,
          borderRadius: '8px',
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        <Box component="span" sx={{ fontSize: '1.1rem', flexShrink: 0 }}>🌱</Box>
        <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.65, color: '#064E3B' }}>
          <strong>Remember:</strong> This report is a snapshot of your karmic blueprint — not a fixed destiny. Every
          section carries both a <strong>diagnosis</strong> (what is) and a <strong>prescription</strong> (what you can do).
          Engage with the remedies, the house-balancing activities, and the opportunity windows to shape your future
          with intention. Astrology is most powerful when it moves you to action.
        </Typography>
      </Box>
    </Box>
  );
};
